import { graphqlClient as rawClient } from "@/apollo";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import { sentryException } from "@/sentry";
import type { DocumentNode } from "graphql";
import type {
  OperationVariables,
  QueryOptions,
  MutationOptions,
} from "@apollo/client";

function getOperationName(doc: DocumentNode): string {
  const def = doc.definitions.find((d) => d.kind === "OperationDefinition");
  return (
    (def as { name?: { value: string } } | undefined)?.name?.value ??
    "UnknownOperation"
  );
}

function buildError(
  operation: string,
  errors:
    | readonly {
        message: string;
        path?: readonly (string | number)[] | null;
        extensions?: Record<string, unknown> | null;
      }[]
    | undefined,
  callerStack: Error,
  cause?: unknown,
): SentryGraphQLError {
  const error = new SentryGraphQLError(operation);

  if (errors?.length) {
    error.graphqlErrors = errors;
    error.message = `${operation}: ${errors[0].message}`;
  } else if (cause !== undefined) {
    const causeMessage = cause instanceof Error ? cause.message : String(cause);
    error.message = `${operation}: ${causeMessage}`;
  }

  if (cause !== undefined) {
    Object.defineProperty(error, "cause", {
      value: cause,
      writable: true,
      configurable: true,
    });
  }

  if (callerStack.stack) {
    error.stack = callerStack.stack.replace(
      /^Error\n/,
      `${error.name}: ${error.message}\n`,
    );
  }

  // Reported once here since this is the single choke point for all GraphQL
  // calls; sentryException dedupes if a caller also reports this instance.
  sentryException(error);

  return error;
}

// We need this for testing purpose on @/api/*.ts files
const graphqlClient = {
  async query<T = unknown, V extends OperationVariables = OperationVariables>(
    options: QueryOptions<V, T>,
  ): Promise<Awaited<ReturnType<typeof rawClient.query<T, V>>>> {
    const callerStack = new Error();
    const operation = getOperationName(options.query as DocumentNode);

    let response: Awaited<ReturnType<typeof rawClient.query<T, V>>>;

    try {
      response = await rawClient.query<T, V>(options);
    } catch (cause) {
      throw buildError(operation, undefined, callerStack, cause);
    }

    if (response.errors?.length) {
      throw buildError(operation, response.errors, callerStack);
    }

    return response;
  },

  async mutate<T = unknown, V extends OperationVariables = OperationVariables>(
    options: MutationOptions<T, V>,
  ): Promise<Awaited<ReturnType<typeof rawClient.mutate<T, V>>>> {
    const callerStack = new Error();
    const operation = getOperationName(options.mutation as DocumentNode);

    let response: Awaited<ReturnType<typeof rawClient.mutate<T, V>>>;

    try {
      response = await rawClient.mutate<T, V>(options);
    } catch (cause) {
      throw buildError(operation, undefined, callerStack, cause);
    }

    if (response.errors?.length) {
      throw buildError(operation, response.errors, callerStack);
    }

    return response;
  },
};

export default graphqlClient;
